import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Search, 
  Bell, 
  ChevronDown, 
  Edit, 
  Copy, 
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  Maximize2,
  Info,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Plus,
  Shirt,
  User,
  Settings,
  Heart,
  Tag,
  Eye,
  Calendar,
  Award,
  Star,
  Check,
  Share2,
  Camera,
  Upload,
  Image as ImageIcon,
  Box,
  Globe,
  Instagram,
  ExternalLink,
  Users,
  Wallet,
  Package,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import ModelPreview from '../components/ModelPreview';
import ModelViewer from '../components/ModelViewer';
import PieceCard from '../components/PieceCard';
import UnifiedPieceCard from '../components/UnifiedPieceCard';
import RequestTradeModal from '../components/RequestTradeModal';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import { useWallet } from '../contexts/WalletContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useFavorites } from '../hooks/useFavorites';
import { useCapsuleFavorites } from '../hooks/useCapsuleFavorites';
import { useWalletFavorites } from '../hooks/useWalletFavorites';
import { useVerification } from '../contexts/VerificationContext';
import { useCreatorPieces } from '../hooks/usePieces';
import { getCreatorProfile, getCreatorProfileByHandle } from '../utils/profileManagement';
import { getAllCapsules, getPiecesByHolder, getCapsuleForPiece } from '../utils/supabaseDatabase';
import { trackCreatorView } from '../utils/creatorViewTracking';
import { normalizePieces } from '../utils/pieceNormalizer';
import { completePurchaseWithDelegate, checkSufficientBalance } from '../utils/marketplaceTransactions';
import { createGroupedPieceCards, isGroupedCard, getAvailablePieceFromGroup } from '../utils/pieceGrouping';
import { getActiveListing, subscribeToListing, subscribeToPiece } from '../utils/resaleMarketplace';
import { createOffer } from '../utils/offersContract';
import GroupedPiecesModal from '../components/GroupedPiecesModal';
import VerifiedBadge from '../components/VerifiedBadge';

const CreatorProfile = () => {
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, connectWallet, wallet } = useWallet();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { toggleFavorite, isFavorited } = useFavorites();
  const { toggleCapsuleFavorite, isCapsuleFavorited } = useCapsuleFavorites();
  const { toggleWalletFavorite, isWalletFavorited } = useWalletFavorites();
  const { userVerificationStatus, checkVerificationStatus } = useVerification();
  const [creator, setCreator] = useState(null);
  const [capsules, setCapsules] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [createdPieces, setCreatedPieces] = useState([]);
  const [ownedPieces, setOwnedPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('pieces');
  const [searchTerm, setSearchTerm] = useState('');
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [groupedPiecesModal, setGroupedPiecesModal] = useState({ isOpen: false, groupedCard: null });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [buyModal, setBuyModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [notForSaleModal, setNotForSaleModal] = useState({ isOpen: false, item: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [sortBy, setSortBy] = useState('price-low');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null);
  const [activeListing, setActiveListing] = useState(null);
  const [listingLoading, setListingLoading] = useState(false);
  const dropdownRef = useRef(null);
  const [creatorWallet, setCreatorWallet] = useState(null);
  
  // Fetch pieces by creator from Supabase
  // NOTE: This queries by creator_id, so pieces ALWAYS appear in the Pieces tab
  // even after being sold (holder_wallet changes, but creator_id stays the same)
  const { pieces: supabasePieces, loading: piecesLoading } = useCreatorPieces(creatorWallet);

  // Helper function to create a File object from base64 data
  const createFileFromDataUrl = (dataUrl, fileName, fileType) => {
    if (!dataUrl) return null;
    
    try {
      const byteString = atob(dataUrl.split(',')[1]);
      const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      const blob = new Blob([ab], { type: mimeString });
      const file = new File([blob], fileName, { type: fileType });
      
      return file;
    } catch (error) {
      console.error('Error creating file from data URL:', error);
      return null;
    }
  };

  useEffect(() => {
    loadCreatorData();
  }, [creatorId]);

  // Sort options
  const sortOptions = [
    { id: 'price-low', name: 'Price low to high' },
    { id: 'price-high', name: 'Price high to low' },
    { id: 'recent', name: 'Recently created' },
    { id: 'sold', name: 'Recently sold' }
  ];

  // Handle dropdown
  const handleSortChange = (optionId) => {
    setSortBy(optionId);
    setIsDropdownOpen(false);
  };

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // Process pieces from Supabase when they load
  useEffect(() => {
    if (supabasePieces && supabasePieces.length > 0) {
      console.log('🔍 CreatorProfile: Processing Supabase pieces:', supabasePieces);
      console.log('🔍 CreatorProfile: Raw piece data:', supabasePieces[0]);
      
      // Normalize pieces to ensure consistent field names
      const normalizedPieces = normalizePieces(supabasePieces);
      console.log('🔍 CreatorProfile: Normalized pieces:', normalizedPieces);
      console.log('🔍 CreatorProfile: First normalized piece:', normalizedPieces[0]);
      
      // Transform pieces with creator data
      const transformedPieces = normalizedPieces.map((piece) => {
        console.log('🔍 CreatorProfile: Processing piece:', {
          name: piece.name,
          thumbnailUrl: piece.thumbnailUrl,
          previewUrl: piece.previewUrl,
          modelUrl: piece.modelUrl,
          preview_glb_url: piece.preview_glb_url,
          thumbnail_url: piece.thumbnail_url
        });
        
        // IMPORTANT: Spread piece first to get ALL fields (including mint_address!),
        // then override specific fields to ensure we don't lose normalized URLs
        const transformed = {
          ...piece, // Get all fields from normalized piece including mint_address, holder_wallet, etc.
          
          // Override/ensure these specific fields are set correctly
          id: piece.id,
          name: piece.name,
          description: piece.description,
          category: piece.category,
          brand: piece.brand,
          color: piece.color,
          rarity: piece.rarity,
          price: piece.price,
          quantity: piece.quantity,
          totalMade: piece.totalMade,
          tags: piece.tags,
          
          // Asset URLs - explicitly preserve from normalized piece
          thumbnailUrl: piece.thumbnailUrl,
          posterUrl: piece.posterUrl,
          previewUrl: piece.previewUrl,
          modelUrl: piece.modelUrl,
          
          // Bucket/key info
          thumbnailBucket: piece.thumbnailBucket,
          thumbnailKey: piece.thumbnailKey,
          previewBucket: piece.previewBucket,
          previewKey: piece.previewKey,
          modelBucket: piece.modelBucket,
          modelKey: piece.modelKey,
          
          // File info
          fileName: piece.fileName,
          fileSize: piece.fileSize,
          fileType: piece.fileType,
          
          // Timestamps
          createdAt: piece.createdAt,
          createdDate: piece.createdAt,
          
          // Additional flags
          has3DModel: piece.has3DModel,
          modelType: piece.modelType,
          isCreated: true,
          
          // Stats
          views: Math.floor(Math.random() * 1000) + 50,
          likes: Math.floor(Math.random() * 100) + 5,
          downloads: Math.floor(Math.random() * 30) + 2,
          
          // Creator info
          originalCreator: piece.creator ? {
            name: piece.creator.handle || 'Creator',
            username: piece.creator.handle || 'creator',
            wallet: piece.creator.wallet,
            piecesCount: piece.creator.total_pieces_created || 0,
            verified: piece.creator.is_verified || false,
            profilePicture: piece.creator.profile_picture_url || null,
            profile_picture_url: piece.creator.profile_picture_url || null
          } : null,
          
          // Keep original for reference
          _original: piece._original
        };
        
        console.log('✅ CreatorProfile: Transformed piece thumbnailUrl:', transformed.thumbnailUrl);
        return transformed;
      });
      
      console.log('✅ CreatorProfile: Transformed pieces:', transformedPieces);
      console.log('✅ CreatorProfile: First transformed piece:', transformedPieces[0]);
      
      // Apply grouping logic (just like Explore page)
      console.log('🔗 Applying grouping logic to creator pieces...');
      const groupedPieces = createGroupedPieceCards(transformedPieces);
      console.log('✅ Grouped creator pieces:', groupedPieces);
      console.log(`📊 Grouping result: ${transformedPieces.length} pieces → ${groupedPieces.length} cards`);
      
      setCreatedPieces(groupedPieces);
      setPieces(groupedPieces);
      
      // Update stats if creator exists
      if (creator) {
        setCreator(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            totalPieces: transformedPieces.length,
            itemsCreated: transformedPieces.length,
            totalItems: transformedPieces.length + (prev.stats?.totalCapsules || 0)
          }
        }));
      }
    }
  }, [supabasePieces]);

  const loadCreatorData = async () => {
    try {
      setLoading(true);
      
      if (!creatorId) {
        console.error('No creator ID provided');
        setLoading(false);
        return;
      }

      // Try to fetch profile from Supabase
      let profileData = null;
      
      // Check if creatorId is a wallet address
      // Solana wallet addresses are typically 32-44 characters long and use base58 encoding
      // If it's shorter than 20 chars or contains special characters, treat it as a username/handle
      const isWalletAddress = creatorId.length >= 20 && /^[1-9A-HJ-NP-Za-km-z]+$/.test(creatorId);
      
      let profileResponse;
      if (isWalletAddress) {
        console.log('🔍 Treating as wallet address:', creatorId);
        profileResponse = await getCreatorProfile(creatorId);
      } else {
        console.log('🔍 Treating as handle/username:', creatorId);
        profileResponse = await getCreatorProfileByHandle(creatorId);
      }

      console.log('Loaded profile response:', profileResponse);

      let userData;
      // Check if the response was successful and has data
      if (profileResponse && profileResponse.success && profileResponse.data) {
        profileData = profileResponse.data;
        console.log('✅ Profile data extracted:', profileData);
        
        // Get verification status from context or check database
        let verificationStatus = { verified: false, status: 'not_requested' };
        const walletKey = profileData.wallet || creatorId;
        
        // Try to get from context first
        if (userVerificationStatus[walletKey]) {
          verificationStatus = userVerificationStatus[walletKey];
        } else if (checkVerificationStatus) {
          // Fallback to checking database
          try {
            const dbStatus = await checkVerificationStatus(walletKey);
            if (dbStatus) {
              verificationStatus = {
                verified: dbStatus.verified,
                status: dbStatus.status
              };
            }
          } catch (error) {
            console.warn('Could not check verification status:', error);
          }
        }
        
        // Transform Supabase profile data to match our component structure
        userData = {
          id: profileData.id || creatorId,
          address: profileData.wallet ? `${profileData.wallet.slice(0, 6)}...${profileData.wallet.slice(-4)}` : creatorId,
          fullAddress: profileData.wallet || creatorId,
          username: profileData.handle || 'Anonymous User',
          bio: profileData.bio || 'Digital fashion enthusiast',
          joinDate: profileData.created_at || new Date().toISOString(),
          balance: 0,
          profilePicture: profileData.profile_picture_url || null,
          banner: profileData.banner_url || null,
          verified: profileData.is_verified || verificationStatus.verified || false,
          verificationType: profileData.verification_type || 'none',
          socialLinks: profileData.social_links || {
            website: '',
            x: '',
            instagram: ''
          },
          stats: {
            totalItems: 0,
            totalValue: 0,
            itemsCreated: 0,
            favorites: 0,
            outfits: 0,
            totalCapsules: 0,
            totalPieces: 0,
            totalViews: profileData.total_views || 0,
            totalLikes: 0
          }
        };

        // Track creator view
        // Use the appropriate identifier type based on how we looked up the profile
        if (publicKey) {
          if (isWalletAddress) {
            trackCreatorView(profileData.wallet || creatorId, 'wallet');
          } else {
            trackCreatorView(profileData.handle || creatorId, 'handle');
          }
        }
      } else {
        // Fallback if profile not found
        userData = {
          id: creatorId,
          address: `${creatorId.slice(0, 6)}...${creatorId.slice(-4)}`,
          fullAddress: creatorId,
          username: creatorId.charAt(0).toUpperCase() + creatorId.slice(1),
          bio: 'Digital fashion enthusiast',
          joinDate: new Date().toISOString(),
          balance: 0,
          profilePicture: null,
          banner: null,
          verified: false,
          socialLinks: {
            website: '',
            x: '',
            instagram: ''
          },
          stats: {
            totalItems: 0,
            totalValue: 0,
            itemsCreated: 0,
            favorites: 0,
            outfits: 0,
            totalCapsules: 0,
            totalPieces: 0,
            totalViews: 0,
            totalLikes: 0
          }
        };
      }

      setCreator(userData);
      
      // Set the creator wallet so the useCreatorPieces hook can fetch pieces
      setCreatorWallet(userData.fullAddress);

      // Load capsules from Supabase
      try {
        const capsulesResponse = await getAllCapsules();
        console.log('Loaded capsules from Supabase:', capsulesResponse);
        
        const capsulesData = capsulesResponse.capsules || [];
        
        // Filter capsules by creator wallet
        const userCapsules = capsulesData.filter(capsule => 
          capsule.creators && capsule.creators.wallet === userData.fullAddress
        );
        
        console.log('Filtered user capsules:', userCapsules);
        
        // Transform capsules to match our data structure
        const transformedCapsules = userCapsules.map(capsule => ({
          id: capsule.id,
          name: capsule.name,
          description: capsule.description,
          banner: capsule.banner || capsule.banner_url,
          image: capsule.banner || capsule.banner_url || capsule.image,
          createdDate: capsule.created_at,
          createdAt: capsule.created_at,
          dropDate: capsule.dropped_at || capsule.created_at, // Use dropped_at, fallback to created_at
          collectionModels: capsule.pieces || [],
          pieceCount: capsule.pieceCount || capsule.piece_count || 0,
          views: Math.floor(Math.random() * 2000) + 100,
          likes: Math.floor(Math.random() * 150) + 10,
          downloads: Math.floor(Math.random() * 50) + 5,
          price: `${(capsule.pieceCount || capsule.piece_count || 0) * 2.5} SOL`,
          avgPrice: capsule.avgPrice || `${(capsule.pieceCount || capsule.piece_count || 0) * 2.5}`,
          status: capsule.status || 'published',
          type: 'capsule'
        }));
        
        setCapsules(transformedCapsules);
        
        // Update stats
        userData.stats.totalCapsules = transformedCapsules.length;
      } catch (error) {
        console.error('Error loading capsules:', error);
        setCapsules([]);
      }

      // Load owned pieces from Supabase
      // IMPORTANT: This queries by holder_wallet, so when a piece is sold:
      // - holder_wallet changes to the buyer's wallet
      // - The piece automatically disappears from seller's Owned tab
      // - The piece stays in seller's Pieces tab (queried by creator_id)
      try {
        console.log('🔍 Loading owned pieces for wallet:', userData.fullAddress);
        const ownedResponse = await getPiecesByHolder(userData.fullAddress, 0, 1000);
        console.log('Loaded owned pieces from Supabase:', ownedResponse);
        
        const ownedPiecesData = ownedResponse.pieces || [];
        
        // Transform owned pieces to match our data structure
        const transformedOwnedPieces = ownedPiecesData.map(piece => ({
          id: piece.id,
          name: piece.name,
          description: piece.description || 'A premium digital fashion piece',
          category: piece.category || 'other',
          color: piece.color,
          // Use actual purchase price (last_sale_price_lamports) first, then fall back to base price
          price: piece.last_sale_price_lamports 
            ? piece.last_sale_price_lamports / 1_000_000_000 
            : (piece.price || 0),
          modelUrl: piece.preview_glb_url || piece.model_url,
          previewUrl: piece.preview_glb_url || piece.model_url,
          thumbnailUrl: piece.thumbnail_url || '/images/piece-placeholder.png',
          posterUrl: piece.poster_url,
          createdDate: piece.created_at,
          createdAt: piece.created_at,
          status: piece.listing_status,
          isCreated: false, // These are owned, not created
          // Include creator and holder info
          creator: piece.creators ? {
            ...piece.creators,
            username: piece.creators.handle || piece.creators.username,
            piecesCount: piece.creators.total_pieces_created || 0,
            verified: piece.creators.is_verified || false,
            profilePicture: piece.creators.profile_picture_url || null
          } : null,
          originalCreator: piece.creators ? {
            ...piece.creators,
            username: piece.creators.handle || piece.creators.username,
            piecesCount: piece.creators.total_pieces_created || 0,
            verified: piece.creators.is_verified || false,
            profilePicture: piece.creators.profile_picture_url || null
          } : null,
          holder: piece.holder_wallet,
          creator_wallet: piece.creator_wallet,
          ...piece
        }));
        
        setOwnedPieces(transformedOwnedPieces);
        console.log('✅ Set owned pieces:', transformedOwnedPieces.length);
        
      } catch (error) {
        console.error('Error loading owned pieces:', error);
        setOwnedPieces([]);
      }

    } catch (error) {
      console.error('Error loading creator data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShareProfile = () => {
    navigator.clipboard.writeText(window.location.href);
    showNotification('Profile link copied to clipboard!', 'success');
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(creator.fullAddress);
      console.log('Address copied to clipboard');
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'website': return <Globe size={16} />;
      case 'x': 
      case 'twitter': return <span className="text-white/80 group-hover:text-white font-bold text-sm">𝕏</span>;
      case 'instagram': return <Instagram size={16} />;
      default: return <Globe size={16} />;
    }
  };

  const getSocialLabel = (platform) => {
    switch (platform) {
      case 'website': return 'Website';
      case 'x': 
      case 'twitter': return 'X (Twitter)';
      case 'instagram': return 'Instagram';
      default: return 'Website';
    }
  };

  const formatSocialUrl = (url, platform) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const validateUrl = (url, platform) => {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  // Filter items based on search term
  const getFilteredItems = () => {
    const itemsToFilter = currentView === 'pieces' ? createdPieces : currentView === 'owned' ? ownedPieces : [];
    
    // Filter by search term
    let filtered = itemsToFilter;
    if (searchTerm.trim()) {
      filtered = itemsToFilter.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const priceA = typeof a.price === 'string' ? parseFloat(a.price.replace(' SOL', '')) : parseFloat(a.price) || 0;
      const priceB = typeof b.price === 'string' ? parseFloat(b.price.replace(' SOL', '')) : parseFloat(b.price) || 0;
      
      switch (sortBy) {
        case 'price-low':
          return priceA - priceB;
        case 'price-high':
          return priceB - priceA;
        case 'recent':
          return new Date(b.createdDate || b.createdAt || 0) - new Date(a.createdDate || a.createdAt || 0);
        case 'sold':
          // For now, sort by recent since we don't have sold date
          return new Date(b.createdDate || b.createdAt || 0) - new Date(a.createdDate || a.createdAt || 0);
        default:
          return 0;
      }
    });
    
    return sorted;
  };

  // Handle buy now button
  const handleBuyNow = (item) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to make a purchase', 'error');
      connectWallet();
      return;
    }
    
    // Check if the item is actually for sale
    if (item.status !== 'listed') {
      setNotForSaleModal({ isOpen: true, item });
      return;
    }
    
    setBuyModal({ isOpen: true, item });
  };

  // Handle request trade button
  const handleRequestTrade = (item) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to request a trade', 'error');
      connectWallet();
      return;
    }
    setRequestTradeModal({ isOpen: true, item });
  };

  // Handle piece detail modal
  const openPieceDetailModal = (item) => {
    console.log('🔍 openPieceDetailModal: Opening modal for item:', item);
    
    // Check if this is a grouped card - if so, show grouped pieces modal instead
    if (isGroupedCard(item)) {
      console.log('🔗 Grouped card detected - showing grouped pieces modal');
      console.log(`  - Group size: ${item._groupSize}`);
      console.log(`  - Available: ${item._availableCount}`);
      setGroupedPiecesModal({ isOpen: true, groupedCard: item });
      return;
    }
    
    // Normalize the piece data to ensure verificationType is correctly mapped
    const normalizedPiece = item ? normalizePieces([item])[0] : null;
    setPieceDetailModal({ isOpen: true, item: normalizedPiece });
    
    // Check if piece has 3D model and set initial view mode
    const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
    
    if (has3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
    setModalViewMode('3d');
    setThreeDStatus('loading');
    setThreeDError(null);
    setActivityPanelOpen(false);
    setPieceCapsuleInfo(null);
  };

  // Fetch capsule information when modal opens
  useEffect(() => {
    if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.id) {
      setPieceCapsuleInfo(null);
      return;
    }

    const fetchCapsuleInfo = async () => {
      try {
        // Extract original ID from prefixed ID (supabase_123 -> 123, local_456_0 -> 456)
        let originalId = pieceDetailModal.item.id;
        const itemIdString = String(pieceDetailModal.item.id);
        
        if (itemIdString.startsWith('supabase_')) {
          originalId = itemIdString.replace('supabase_', '');
          // Convert to number if it's a numeric ID
          if (!isNaN(originalId)) {
            originalId = parseInt(originalId, 10);
          }
        } else if (itemIdString.startsWith('local_')) {
          // For local pieces, they typically don't belong to capsules
          console.log('🔍 Local piece detected, skipping capsule check');
          setPieceCapsuleInfo(null);
          return;
        }
        
        console.log('🔍 Checking capsule for piece:', { originalId, transformedId: pieceDetailModal.item.id });
        const capsuleInfo = await getCapsuleForPiece(originalId);
        console.log('🔍 Capsule info result:', capsuleInfo);
        setPieceCapsuleInfo(capsuleInfo);
      } catch (error) {
        console.error('Error fetching capsule info for piece:', error);
        setPieceCapsuleInfo(null);
      }
    };

    fetchCapsuleInfo();
  }, [pieceDetailModal.isOpen, pieceDetailModal.item]);

  // Fetch active listing when piece detail modal opens
  useEffect(() => {
    const fetchActiveListing = async () => {
      if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
        setActiveListing(null);
        return;
      }

      try {
        setListingLoading(true);
        const listing = await getActiveListing(pieceDetailModal.item.mint_address);
        setActiveListing(listing);
      } catch (error) {
        console.error('Error fetching active listing:', error);
        setActiveListing(null);
      } finally {
        setListingLoading(false);
      }
    };

    fetchActiveListing();
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  // Real-time subscription for listing and piece changes
  useEffect(() => {
    if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
      return;
    }
    
    console.log('👁️ Setting up real-time subscriptions for:', pieceDetailModal.item.mint_address);
    
    // Subscribe to listing changes
    const listingSubscription = subscribeToListing(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Listing update received:', payload);
      
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        if (payload.new.status === 'active') {
          setActiveListing(payload.new);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'listed',
              listing_price: payload.new.price_sol
            }
          }));
        } else if (payload.new.status === 'filled') {
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'sold',
              holder_wallet: payload.new.buyer_wallet
            }
          }));
        } else if (payload.new.status === 'canceled') {
          // Listing canceled - remove buy button
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'unlisted',
              listing_price: null
            }
          }));
        }
      } else if (payload.eventType === 'DELETE') {
        setActiveListing(null);
      }
    });
    
    // Subscribe to piece changes
    const pieceSubscription = subscribeToPiece(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Piece update received:', payload);
      
      if (payload.eventType === 'UPDATE') {
        setPieceDetailModal(prev => ({
          ...prev,
          item: {
            ...prev.item,
            ...payload.new
          }
        }));
      }
    });
    
    // Cleanup subscriptions
    return () => {
      console.log('🔌 Cleaning up subscriptions...');
      listingSubscription.unsubscribe();
      pieceSubscription.unsubscribe();
    };
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  // Handle toggle favorite
  const handleToggleFavorite = (item) => {
    toggleFavorite(item.id);
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Process purchase with wallet - REAL BLOCKCHAIN TRANSACTION
  const processPurchase = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item } = buyModal;
    const price = parseFloat(item.price);
    
    // Check sufficient balance (with buffer for gas fees)
    const balanceCheck = await checkSufficientBalance({ connected: isConnected, publicKey }, price);
    if (!balanceCheck.sufficient) {
      showNotification(`Insufficient balance. You need ${balanceCheck.required.toFixed(4)} SOL (including gas) but only have ${balanceCheck.balance.toFixed(4)} SOL`, 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Execute the complete purchase flow on blockchain
      console.log(`🛒 Purchasing ${item.name} for ${price} SOL...`);
      
      const result = await completePurchaseWithDelegate(
        { connected: isConnected, publicKey, sendTransaction: window.solana?.signAndSendTransaction || window.solflare?.signAndSendTransaction },
        item,
        price
      );
      
      // Show success notification
      showNotification(
        `Successfully purchased ${item.name} for ${price.toFixed(2)} SOL! 🎉`,
        'success'
      );
      
      // Log detailed transaction info to console
      console.log('✅ Purchase Details:', {
        item: item.name,
        price: result.feeBreakdown.salePriceFormatted + ' SOL',
        platformFee: result.feeBreakdown.feeAmountFormatted + ' SOL',
        sellerReceived: result.feeBreakdown.sellerReceivesFormatted + ' SOL',
        paymentTx: result.paymentTransaction.signature,
        nftTx: result.nftTransaction?.signature || 'Not minted on-chain yet',
        explorerUrl: result.paymentTransaction.explorerUrl
      });
      
      // Close modal
      setBuyModal({ isOpen: false, item: null });
      
      // Reload page to show updated ownership
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      
      // Handle delegate mismatch error specifically
      if (error.message && error.message.includes('delegate')) {
        showNotification(
          `This piece is not properly listed for sale. The seller needs to re-list it. Please try a different piece.`,
          'error'
        );
      } else {
        showNotification(`Purchase failed: ${error.message}. Please check your wallet and try again.`, 'error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close buy modal
  const closeBuyModal = () => {
    setBuyModal({ isOpen: false, item: null });
  };

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Handle make offer button
  const handleMakeOffer = (item) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to make an offer', 'error');
      connectWallet();
      return;
    }
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7 });
  };

  // Process offer with wallet
  const processOffer = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item, offerAmount, expirationDays } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    if (!offerPrice || offerPrice <= 0 || isNaN(offerPrice)) {
      showNotification('Please enter a valid offer amount', 'error');
      return;
    }

    if (balance < offerPrice) {
      showNotification(`Insufficient balance. You need ${offerPrice} SOL but only have ${balance.toFixed(4)} SOL`, 'error');
      return;
    }

    // Validate required fields
    if (!item.id || !item.mint_address) {
      showNotification('This piece is not ready for offers yet. Please try again later.', 'error');
      return;
    }

    // Extract the real piece ID (remove 'supabase_' prefix if present)
    let realPieceId = item.id;
    if (typeof item.id === 'string' && item.id.startsWith('supabase_')) {
      realPieceId = parseInt(item.id.replace('supabase_', ''));
    }

    setIsProcessing(true);
    
    try {
      console.log(`📝 Creating offer for ${item.name} of ${offerPrice} SOL`);
      
      // Create offer using the real database function
      const result = await createOffer(wallet, {
        pieceId: realPieceId,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '',
        expirationDays: expirationDays || 7,
      });
      
      if (!result.success) {
        // Check if it's a duplicate offer error
        if (result.error && result.error.includes('already have an active offer')) {
          showNotification(
            'You already have an active offer on this piece. Visit the Offers page to view or update it.',
            'error'
          );
          setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
          return;
        }
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      
      // Show success notification
      showNotification(`Offer of ${offerPrice} SOL submitted successfully! 🎉`, 'success');
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      
    } catch (error) {
      console.error('❌ Offer creation failed:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Unable to create offer. Please try again.';
      
      // Check if error message contains the duplicate offer info
      if (error.message && error.message.includes('already have an active offer')) {
        errorMessage = 'You already have an active offer on this piece. Visit the Offers page to view or update it.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
      
      // Close modal if it's a duplicate offer error
      if (errorMessage.includes('already have an active offer')) {
        setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading profile...</div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">👤</div>
          <h1 className="text-2xl font-bold text-white mb-2">Creator Not Found</h1>
          <p className="text-gray-400 mb-6">This creator doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/explore')}
            className="px-6 py-3 bg-white hover:bg-gray-200 text-gray-900 rounded-xl font-medium transition-colors"
          >
            Back to Explore
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Banner Section */}
      <div className="relative">
        <div className="aspect-[1400/400] bg-black flex items-center justify-center overflow-hidden">
        {creator.banner ? (
          <img 
            src={creator.banner} 
            alt="Profile Banner" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-purple-900 via-purple-800 to-blue-800"></div>
        )}
          
          {/* Action Buttons - Bottom Right */}
          <div className="absolute bottom-4 right-4 flex space-x-2 z-20">
                <button
                  onClick={() => toggleWalletFavorite(creator.id, {
                    id: creator.id,
                    username: creator.username,
                    walletAddress: creator.walletAddress || creator.address,
                    piecesCount: creator.stats?.totalPieces || 0,
                    followers: creator.stats?.followers || 0,
                    following: creator.stats?.following || 0,
                    verified: creator.verified || false,
                    verificationType: creator.verificationType || 'none',
                    avatar: creator.profilePicture,
                    bio: creator.bio || '',
                    joinedDate: creator.joinDate,
                    totalVolume: creator.stats?.totalValue || 0,
                    avgPrice: 0
                  })}
              className="p-2 rounded-lg transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg relative z-10 bg-black/60"
                  title={isWalletFavorited(creator.id) ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart 
                size={20} 
                    className={`${
                      isWalletFavorited(creator.id) 
                        ? 'text-red-500 fill-red-500' 
                    : 'text-white/70 hover:text-red-500'
                    } transition-all duration-300`} 
                  />
                </button>
                  <button 
                    onClick={handleCopyAddress}
              className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg"
              title="Copy wallet address"
                  >
              <Copy size={20} />
                  </button>
                  <button 
                    onClick={handleShareProfile}
              className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg"
                    title="Share profile"
                  >
              <Share2 size={20} />
                  </button>
                </div>
              </div>

        {/* Creator Info Overlay */}
        <div className="absolute bottom-2 left-4 right-4">
          <div className="bg-black/70 rounded-xl p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={creator.profilePicture || '/images/piece-placeholder.png'}
                  alt={creator.username || 'Creator'}
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                />
              <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">
                      {creator.username || 'Anonymous User'}
                    </span>
                    {creator.verified && (
                      <VerifiedBadge 
                        size="md" 
                        verificationType={creator.verificationType || creator.verification_type || 'none'}
                      />
                    )}
                  </div>
                  <p className="text-gray-300 text-sm">{creator.bio}</p>
                </div>
              </div>
              
              {/* Social Links */}
              {Object.values(creator.socialLinks || {}).some(link => link && link.trim()) && (
                <div className="flex gap-2 items-center">
                  {Object.entries(creator.socialLinks || {}).map(([platform, url]) => {
                    if (!url || !url.trim()) return null;
                        const formattedUrl = formatSocialUrl(url, platform);
                        return (
                          <a
                            key={platform}
                            href={formattedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        className="group relative flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all duration-300 hover:scale-105"
                            title={getSocialLabel(platform)}
                          >
                        <div className="flex items-center justify-center w-4 h-4 text-white/80 group-hover:text-white transition-colors duration-300">
                              {getSocialIcon(platform)}
                            </div>
                          </a>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Profile Content */}
      <div className="p-6">


        {/* Storefront Content */}
        <div className="space-y-6">
          {/* Navigation Tabs */}
          <div className="border-b border-[#2a2a2a]">
            <nav className="flex space-x-8">
              <button 
                onClick={() => setCurrentView('pieces')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'pieces' 
                    ? 'border-white text-white' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                Created ({createdPieces.length})
              </button>
              <button 
                onClick={() => setCurrentView('capsules')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'capsules' 
                    ? 'border-white text-white' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                Capsules ({capsules.length})
              </button>
              <button 
                onClick={() => setCurrentView('owned')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentView === 'owned' 
                    ? 'border-white text-white' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                Owned ({ownedPieces.length})
              </button>
            </nav>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by piece or tag"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 px-4 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300">Sort by:</span>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[180px]"
                  >
                    <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ml-2 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {sortOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleSortChange(option.id)}
                          className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                            option.id === sortBy
                              ? 'bg-white/10 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{option.name}</span>
                          {option.id === sortBy && (
                            <Check size={16} className="text-white" />
                          )}
                        </button>
                      ))}
                </div>
                  )}
              </div>
            </div>
            </div>
          </div>


          {/* Content Display */}
          <div className="space-y-6">
            <div className="px-6 py-4 mb-6">
              <h2 className="text-2xl font-bold text-white mb-1">
                {currentView === 'pieces' ? 'Created' : currentView === 'capsules' ? 'Capsules' : 'Owned'}
              </h2>
              <p className="text-gray-400 text-sm">
                {currentView === 'pieces' ? getFilteredItems().length : 
                 currentView === 'capsules' ? capsules.length :
                 currentView === 'owned' ? getFilteredItems().length : 0} Pieces
              </p>
            </div>

            {currentView === 'pieces' && (
              <div>
                {getFilteredItems().length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
                      {getFilteredItems().map((item) => {
                        // Transform item data to match expected format
                        const priceValue = typeof item.price === 'string' ? item.price.replace(' SOL', '') : item.price;
                        
                        console.log('🎴 Rendering card for piece:', item.name, 'thumbnailUrl:', item.thumbnailUrl);
                        
                        const pieceData = {
                          ...item,
                          price: priceValue
                        };
                        
                        // Check if current user owns this piece
                        const isOwner = isConnected && publicKey && 
                          pieceData?.holder_wallet === publicKey.toString();
                        
                        // Determine if piece is owned by creator (primary sale)
                        const creatorWallet = pieceData?.creator_wallet || 
                                            pieceData?.creators?.wallet ||
                                            pieceData?.originalCreator?.wallet;
                        const isOwnedByCreator = pieceData?.holder_wallet === creatorWallet;
                        
                        // Show buy button only if user doesn't own it AND (piece is owned by creator OR listed for resale)
                        const canBuy = !isOwner && (isOwnedByCreator || pieceData?.listing_status === 'listed');
                        
                        // Show trade button only if user doesn't own it
                        const canTrade = !isOwner;
                        
                        return (
                          <UnifiedPieceCard
                            key={item.id}
                            item={pieceData}
                            onClick={openPieceDetailModal}
                            onBuy={handleBuyNow}
                            onTrade={handleRequestTrade}
                            onFavorite={handleToggleFavorite}
                            solPrice={solPrice}
                            showBuyButton={canBuy}
                            showTradeButton={canTrade}
                            showFavoriteButton={true}
                            buyButtonText="Buy now"
                            tradeButtonText="Trade"
                            isFavorited={isFavorited(pieceData.id)}
                          />
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <User className="w-10 h-10 text-gray-600" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-400 mb-2">No pieces yet</h3>
                    <p className="text-gray-500 mb-6">This creator hasn't created any pieces yet</p>
                  </div>
                )}
              </div>
            )}

            {currentView === 'capsules' && (
              <div>
                {capsules.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {capsules.map((capsule) => (
                      <Link
                        key={capsule.id}
                        to={`/capsule/${capsule.id}`}
                        className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
                      >
                        {/* Banner Image - Full width, no padding */}
                        <div className="relative aspect-[1400/400] bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                          {capsule.banner ? (
                            <img 
                              src={capsule.banner} 
                              alt={capsule.name}
                              className="w-full h-full object-cover"
                            />
                          ) : capsule.image ? (
                            <img 
                              src={capsule.image} 
                              alt={capsule.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-4xl opacity-30">📦</div>
                          )}
                        </div>

                        {/* Bottom Section with Info */}
                        <div className="p-3 space-y-2">
                          {/* Title with Number */}
                          <h3 className="text-white font-bold text-sm line-clamp-1">
                            {capsule.name} {capsule.capsuleNumber}
                          </h3>

                          {/* Description */}
                          <p className="text-gray-400 text-xs line-clamp-2">
                            {capsule.description || `A curated collection of ${capsule.pieceCount || 0} digital fashion pieces with modern styling and premium quality.`}
                          </p>

                          {/* Stats Row */}
                          <div className="flex items-center justify-between text-xs text-gray-400">
                            <div className="flex items-center gap-3">
                              <span className="flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                                {capsule.pieceCount || 0} pieces
                              </span>
                            </div>
                          </div>

                          {/* Price Row */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-gray-400">Avg Price</div>
                            <div className="text-green-400 font-semibold text-sm">
                              {(() => {
                                const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                // Show more precision for prices < 0.1 SOL
                                if (price > 0 && price < 0.1) {
                                  return price.toFixed(3);
                                }
                                return isNaN(price) ? '0.0' : price.toFixed(1);
                              })()} SOL
                            </div>
                            </div>
                            <div className="text-right text-xs text-gray-400">
                              {(() => {
                                const price = parseFloat(capsule.avgPrice || capsule.price || 0);
                                const usdPrice = isNaN(price) ? 0 : price * solPrice;
                                return usdPrice > 0 ? `~$${usdPrice.toFixed(2)} USD` : '';
                              })()}
                            </div>
                          </div>

                          {/* Drop Date */}
                          <div className="text-xs text-gray-500">
                            Dropped {capsule.dropDate ? new Date(capsule.dropDate).toLocaleDateString('en-US', { 
                              year: '2-digit', 
                              month: '2-digit', 
                              day: '2-digit' 
                            }) : capsule.createdAt ? new Date(capsule.createdAt).toLocaleDateString('en-US', { 
                              year: '2-digit', 
                              month: '2-digit', 
                              day: '2-digit' 
                            }) : 'Recently'}
                          </div>
                        </div>
                        
                        {/* Heart Icon */}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleCapsuleFavorite(capsule);
                          }}
                          className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        >
                          <Heart 
                            size={16} 
                            className={`${
                              isCapsuleFavorited(capsule.id) 
                                ? 'text-red-500 fill-red-500' 
                                : 'text-white/70 hover:text-red-500'
                            } transition-all duration-300`} 
                          />
                        </button>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <User className="w-10 h-10 text-gray-600" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-400 mb-2">No capsules yet</h3>
                    <p className="text-gray-500 mb-6">This creator hasn't created any capsules yet</p>
                  </div>
                )}
              </div>
            )}

            {currentView === 'owned' && (
              <div>
                {getFilteredItems().length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-6">
                    {getFilteredItems().map((item) => {
                      // Transform item data to match expected format
                      const priceValue = typeof item.price === 'string' ? item.price.replace(' SOL', '') : item.price;
                      
                      console.log('🎴 Rendering owned card for piece:', item.name, 'thumbnailUrl:', item.thumbnailUrl);
                      
                      const pieceData = {
                        ...item,
                        price: priceValue
                      };
                      
                      // Check if current user owns this piece
                      const isOwner = isConnected && publicKey && 
                        pieceData?.holder_wallet === publicKey.toString();
                      
                      // Determine if piece is owned by creator (primary sale)
                      const creatorWallet = pieceData?.creator_wallet || 
                                          pieceData?.creators?.wallet ||
                                          pieceData?.originalCreator?.wallet;
                      const isOwnedByCreator = pieceData?.holder_wallet === creatorWallet;
                      
                      // Show buy button only if user doesn't own it AND piece is actually listed for sale
                      const canBuy = !isOwner && 
                        (pieceData?.status === 'listed' || pieceData?.listing_status === 'listed') &&
                        pieceData?.mint_address;
                      
                      // Show trade button only if user doesn't own it
                      const canTrade = !isOwner;
                      
                      return (
                        <UnifiedPieceCard
                          key={item.id}
                          item={pieceData}
                          onClick={openPieceDetailModal}
                          onBuy={handleBuyNow}
                          onTrade={handleRequestTrade}
                          onFavorite={handleToggleFavorite}
                          solPrice={solPrice}
                          showBuyButton={canBuy}
                          showTradeButton={canTrade}
                          showFavoriteButton={true}
                          showEditionBadges={false}
                          buyButtonText="Buy now"
                          tradeButtonText="Trade"
                          isFavorited={isFavorited(pieceData.id)}
                        />
                      );
                    })}
                  </div>
                ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-gray-600" />
                </div>
                    <h3 className="text-xl font-medium text-gray-400 mb-2">No owned pieces yet</h3>
                    <p className="text-gray-500 mb-6">This creator hasn't purchased any pieces yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Buy Modal */}
      {buyModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>

            {/* Item Name */}
            <h4 className="text-white font-medium text-lg mb-4">{buyModal.item?.name}</h4>
            
            {(() => {
              const itemPrice = parseFloat(buyModal.item?.price || '0');
              if (!itemPrice || itemPrice <= 0) {
                return (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
                    <p className="text-red-400 text-sm">
                      ⚠️ This piece has no price set. Cannot purchase.
                    </p>
                  </div>
                );
              }
              return (
                <>
                  {/* Price Breakdown */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-blue-400">Price</span>
                      <div className="text-right">
                        <div className="text-green-400 font-bold text-lg">{itemPrice.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${(itemPrice * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400">Sale Fee (2.5%)</span>
                      <div className="text-right">
                        <div className="text-orange-400 font-medium">
                          {(itemPrice * 0.025).toFixed(4)} SOL
                        </div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${((itemPrice * 0.025) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-gray-400">Seller Receives</span>
                      <div className="text-right">
                        <div className="text-white font-medium">
                          {(itemPrice * 0.975).toFixed(4)} SOL
                        </div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${((itemPrice * 0.975) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Information */}
                  <div className="mb-6">
                    <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Connected Wallet</span>
                        <div className="flex items-center gap-2">
                          <CheckCircle size={16} className="text-green-400" />
                          <span className="text-green-400 text-sm font-medium">
                            {isConnected ? 'Connected' : 'Not Connected'}
                          </span>
                        </div>
                      </div>
                      {isConnected && publicKey && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Address</span>
                            <span className="text-white text-sm font-mono">
                              {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-blue-400 text-sm">Balance</span>
                            <div className="text-right">
                              <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                              <div className="text-gray-500 text-xs">
                                ≈ ${(balance * solPrice).toFixed(2)} USD
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Info Message */}
                  <div className="text-gray-300 text-sm mb-6">
                    You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
                  </div>
                </>
              );
            })()}
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processPurchase}
                disabled={isProcessing || !isConnected || balance < parseFloat(buyModal.item?.price || '0')}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    Confirm Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grouped Pieces Modal */}
      {groupedPiecesModal.isOpen && (
        <GroupedPiecesModal
          isOpen={groupedPiecesModal.isOpen}
          onClose={() => setGroupedPiecesModal({ isOpen: false, groupedCard: null })}
          groupedCard={groupedPiecesModal.groupedCard}
          onBuyNow={handleBuyNow}
          onMakeOffer={handleMakeOffer}
          onRequestTrade={handleRequestTrade}
          solPrice={solPrice || 195.84}
          wallet={window.solana || window.solflare}
          publicKey={publicKey}
          isConnected={isConnected}
        />
      )}

      {/* Piece Detail Modal */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
              {/* Left side - 3D Model */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {(() => {
                  const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                  
                  if (has3DModel) {
                    return (
                      <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                        <button
                          onClick={() => setModalViewMode('3d')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === '3d'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          3D Model
                        </button>
                        <button
                          onClick={() => setModalViewMode('thumbnail')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === 'thumbnail'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          Thumbnail
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square">
                  {(() => {
                    const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                    
                    if (modalViewMode === '3d') {
                      // Try to get bucket/key info from the piece data
                      const modelBucket = pieceDetailModal.item?.preview_bucket || pieceDetailModal.item?.model_bucket;
                      const modelKey = pieceDetailModal.item?.preview_key || pieceDetailModal.item?.model_key;
                      
                      // Fallback to legacy URL if bucket/key not available
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        // You can customize orientation per model if needed
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc) {
                        // You can customize orientation per model if needed
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return pieceDetailModal.item?.thumbnailUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnailUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', pieceDetailModal.item.name, 'URL:', pieceDetailModal.item.thumbnailUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Holder Section */}
                <div>
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(pieceDetailModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                          <img
                            src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                            alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                          </div>
                          {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                            <img 
                              src="/images/Verification badge.png" 
                              alt="Verified" 
                              className="w-4 h-4 object-contain flex-shrink-0"
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty - Outside button */}
                    {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}

                    {/* Social Links */}
                    {(() => {
                      // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                      const socialLinks = pieceCapsuleInfo 
                        ? {
                            website: pieceCapsuleInfo.website,
                            twitter: pieceCapsuleInfo.twitter,
                            instagram: pieceCapsuleInfo.instagram,
                            discord: pieceCapsuleInfo.discord
                          }
                        : (pieceDetailModal.item?.social_links || {});
                      const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      
                      // DEBUG: Log social links data
                      console.log('🔍 [CREATOR PROFILE] Social Links Debug:', {
                        pieceId: pieceDetailModal.item?.id,
                        pieceName: pieceDetailModal.item?.name,
                        pieceCapsuleInfo: pieceCapsuleInfo,
                        usingCapsuleLinks: !!pieceCapsuleInfo,
                        social_links: socialLinks,
                        hasSocialLinks
                      });
                      
                      if (!hasSocialLinks) return null;
                      
                      return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                      {socialLinks.website && (
                        <a
                          href={socialLinks.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Website"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.twitter && (
                        <a
                          href={socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="X (Twitter)"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.instagram && (
                        <a
                          href={socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Instagram"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.discord && (
                        <a
                          href={socialLinks.discord}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Discord"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                        </a>
                      )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(pieceDetailModal.item.id);
                        }}
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title={isFavorited(pieceDetailModal.item.id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Heart 
                          size={20} 
                          className={`transition-all duration-300 ${
                            isFavorited(pieceDetailModal.item.id)
                              ? 'text-red-500 fill-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    {/* Custom Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                        pieceDetailModal.item.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                        activityPanelOpen 
                          ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                          : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                      }`}
                    >
                      Activity
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm">
                          {pieceDetailModal.item?.category || 'Uncategorized'}
                        </span>
                      </div>
                      {pieceDetailModal.item?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize">{pieceDetailModal.item.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <h3 className="text-white font-semibold mb-2">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {pieceDetailModal.item?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <h3 className="text-white font-semibold mb-3">Pricing</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {(() => {
                            const item = pieceDetailModal.item;
                            const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                            return displayPrice;
                          })()} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${(() => {
                          const item = pieceDetailModal.item;
                          const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                          return ((parseFloat(displayPrice) || 0) * solPrice).toFixed(2);
                        })()} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {pieceDetailModal.item?.quantity || 1}
                    </div>
                  </div>

                  {/* Created Date */}
                  <div className="text-gray-500 text-sm">
                    Created {pieceDetailModal.item?.createdDate ? new Date(pieceDetailModal.item.createdDate).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-2">
                    {/* Buy Now Button - Only show for available pieces */}
                    {(() => {
                      const isOwner = isConnected && publicKey && 
                        pieceDetailModal.item?.holder_wallet === publicKey.toString();
                      
                      // Determine if piece is owned by creator (primary sale)
                      const creatorWallet = pieceDetailModal.item?.creator_wallet || 
                                          pieceDetailModal.item?.creators?.wallet ||
                                          pieceDetailModal.item?.originalCreator?.wallet;
                      const isOwnedByCreator = pieceDetailModal.item?.holder_wallet === creatorWallet;
                      
                      // Don't show Buy Now if user owns it
                      if (isOwner) {
                        return (
                          <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30">
                            <span className="text-sm font-medium">✓ You own this piece</span>
                          </div>
                        );
                      }
                      
                      return (
                        <>
                          {/* Show buy button for primary sales from creator */}
                          {isOwnedByCreator && !isOwner && 
                           (pieceDetailModal.item?.status === 'listed' || pieceDetailModal.item?.listing_status === 'listed') &&
                           pieceDetailModal.item?.mint_address && (
                            <button 
                              onClick={() => handleBuyNow(pieceDetailModal.item)}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                            >
                              Buy Now - {(() => {
                                const item = pieceDetailModal.item;
                                const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                                return displayPrice;
                              })()} SOL
                            </button>
                          )}
                          
                          {/* Show buy button for resale listings */}
                          {!isOwnedByCreator && !isOwner && 
                           pieceDetailModal.item?.listing_status === 'listed' &&
                           pieceDetailModal.item?.listing_price &&
                           pieceDetailModal.item?.mint_address && (
                            <button
                              onClick={() => handleBuyNow(pieceDetailModal.item)}
                              disabled={!isConnected}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                              Buy Now - {(pieceDetailModal.item?.listing_price || pieceDetailModal.item?.price || 0).toFixed(4)} SOL
                            </button>
                          )}
                          
                          {/* Show not for sale status */}
                          {!isOwner && 
                           pieceDetailModal.item?.listing_status !== 'listed' && 
                           pieceDetailModal.item?.status !== 'listed' &&
                           pieceDetailModal.item?.listing_status !== 'sold' && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              Not for Sale
                            </div>
                          )}
                          
                          {/* Show sold status only if viewer is NOT the current owner */}
                          {pieceDetailModal.item?.listing_status === 'sold' && !isOwner && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              ✅ Sold
                            </div>
                          )}
                          
                          {/* Not connected message */}
                          {!isConnected && (isOwnedByCreator || pieceDetailModal.item?.listing_status === 'listed') && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center text-sm">
                              Connect your wallet to purchase
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Original buttons - Make Offer and Request Trade */}
                    {(() => {
                      const isOwner = isConnected && publicKey && 
                        pieceDetailModal.item?.holder_wallet === publicKey.toString();
                      
                      // Don't show any action buttons if user owns the piece
                      if (isOwner) {
                        return null;
                      }
                      
                      return (
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleMakeOffer(pieceDetailModal.item)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                          >
                            Make Offer
                          </button>
                          <button 
                            onClick={() => handleRequestTrade(pieceDetailModal.item)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a] flex items-center justify-center gap-2"
                          >
                            <ArrowLeftRight size={16} />
                            Request Trade
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={pieceDetailModal.item?.id}
                    pieceName={pieceDetailModal.item?.name}
                    basePrice={parseFloat(pieceDetailModal.item?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {offerModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
                <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">List Price</span>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">{offerModal.item?.price} SOL</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(offerModal.item?.price || '0') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Offer Amount Input */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Your Offer Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offerModal.offerAmount}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, offerAmount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                  <div className="mt-2 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(offerModal.offerAmount) * solPrice).toFixed(2)} USD
                  </div>
                )}
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                Your offer will be locked in escrow until the seller accepts or rejects it. You can cancel your offer at any time.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeOfferModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processOffer}
                disabled={isProcessing || !isConnected || !offerModal.offerAmount || isNaN(parseFloat(offerModal.offerAmount)) || parseFloat(offerModal.offerAmount) <= 0 || balance < parseFloat(offerModal.offerAmount || '0')}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Package size={16} />
                    Submit Offer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Trade Modal */}
      <RequestTradeModal
        isOpen={requestTradeModal.isOpen}
        onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
        targetPiece={requestTradeModal.item}
        onRequestTrade={(tradeRequest) => {
          console.log('Trade request submitted:', tradeRequest);
          setRequestTradeModal({ isOpen: false, item: null });
          setPieceDetailModal({ isOpen: false, item: null });
        }}
        onSuccess={(message) => {
          console.log('Trade request sent successfully');
          showNotification(message || 'Trade request sent successfully! 🎉', 'success');
        }}
      />

      {/* Not For Sale Modal */}
      {notForSaleModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="text-center">
              {/* Icon */}
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info size={32} className="text-red-500" />
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-white mb-2">
                Not Available for Purchase
              </h3>
              
              {/* Message */}
              <p className="text-gray-400 mb-6 leading-relaxed">
                This piece is not currently for sale. The holder hasn't listed it for sale, but you can make an offer or request a trade!
              </p>
              
              {/* Action Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => setNotForSaleModal({ isOpen: false, item: null })}
                  className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : notification.type === 'error' ? (
                  <AlertCircle size={20} className="text-red-400" />
                ) : (
                  <AlertCircle size={20} className="text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorProfile;
